import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, MagnifyingGlass, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

// Fix default marker icons for react-leaflet in bundlers
const defaultIcon = L.icon({
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function ClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView([center.lat, center.lng], 15); }, [center, map]);
  return null;
}

export default function ChantierGpsPicker({ chantier, onSaved }) {
  const initial = chantier?.latitude != null && chantier?.longitude != null
    ? { lat: chantier.latitude, lng: chantier.longitude }
    : { lat: 43.6108, lng: 3.8767 }; // Montpellier default
  const [pos, setPos] = useState(chantier?.latitude != null ? { lat: chantier.latitude, lng: chantier.longitude } : null);
  const [center, setCenter] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState(chantier?.address || "");
  const [searching, setSearching] = useState(false);
  // Memo the map center + marker position arrays so react-leaflet doesn't
  // re-init the map/marker on every re-render.
  const mapCenter = useMemo(() => [center.lat, center.lng], [center.lat, center.lng]);
  const markerPos = useMemo(() => (pos ? [pos.lat, pos.lng] : null), [pos]);

  // GPS captation from the browser is disabled — the picker now relies on
  // manual address geocoding + map click only. Karim 2026-02-19.

  const geocode = async () => {
    if (!searchText.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(searchText)}`, {
        headers: { "Accept-Language": "fr" },
      });
      const arr = await r.json();
      if (!arr || arr.length === 0) { toast.error("Adresse introuvable"); return; }
      const first = arr[0];
      const c = { lat: parseFloat(first.lat), lng: parseFloat(first.lon) };
      setPos(c);
      setCenter(c);
      toast.success("Adresse localisée");
    } catch (err) { toast.error("Erreur de géocodage"); }
    finally { setSearching(false); }
  };

  const save = async () => {
    if (!pos) { toast.error("Choisissez d'abord une position"); return; }
    setSaving(true);
    try {
      await api.patch(`/chantiers/${chantier.id}`, { latitude: pos.lat, longitude: pos.lng });
      toast.success("GPS enregistré · géofence actif");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2" data-testid="chantier-gps-picker">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5"><MapPin size={14} weight="duotone" className="text-[#005CFF]" /> Position GPS</div>
          <div className="text-[10px] text-white/50 mt-0.5">Saisis une adresse ou clique sur la carte (la géoloc du navigateur est désactivée &middot; la position vient de l&apos;app mobile)</div>
        </div>
        {pos && <span className="text-[10px] text-emerald-400 font-mono">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</span>}
      </div>

      <div className="flex gap-1.5">
        <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && geocode()} placeholder="Adresse à géocoder…" className="flex-1 bg-white/5 border-white/10 h-9 text-xs" data-testid="gps-address-input" />
        <Button onClick={geocode} disabled={searching} variant="ghost" className="border border-white/10 h-9 px-2" data-testid="gps-geocode-btn"><MagnifyingGlass size={13} /></Button>
      </div>

      <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: 220 }}>
        <MapContainer center={mapCenter} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickHandler onClick={(p) => setPos(p)} />
          <Recenter center={center} />
          {markerPos && <Marker position={markerPos} icon={defaultIcon} />}
        </MapContainer>
      </div>

      <Button onClick={save} disabled={saving || !pos} className="w-full bg-[#005CFF] hover:bg-[#0047CC] h-10" data-testid="gps-save-btn">
        <FloppyDisk size={14} /> {saving ? "…" : "Enregistrer la position"}
      </Button>
    </div>
  );
}
